/**
 * batch-68 atv-tvt-uitleg (FO-344) - ATV en TVT in gewone taal.
 *
 * De vorm (woordenlijst, keuzes en DTO's) van de uitleg-laag rond de
 * werktijd- en spaarinstellingen van een medewerker. De rekenregels zelf
 * veranderen niet: de opbouwfactor blijft `tvtFactor` in
 * `@streep/utils/payroll-calculations.ts`. De zinnen en rekenvoorbeelden komen
 * uit één pure helper (`@streep/utils/werktijd-sparen-uitleg.ts`); dit bestand
 * is uitsluitend de vorm, zodat portal, backoffice en API dezelfde woorden en
 * dezelfde DTO's delen.
 *
 * Server-veilig: geen React, geen 'use client'.
 */

import { z } from 'zod'
import { idParam } from './id-validators'

// ── Woordenlijst (FO-344 §3.4) ───────────────────────────────────────────────
//
// Eén bron voor de woorden in portal én backoffice. `label` is de volledige
// term (formulieren, uitleg), `kort` is het label voor tegels en kolommen. De
// afkortingen ATV en TVT blijven tussen haakjes staan als herkenningspunt: ze
// staan op de loonstrook en in de CAO.

export const WERKTIJD_SPAREN_LABELS = {
  /** `atvEnabled` - ATV-opbouw in de 40-urenweek. */
  atv: { label: 'ATV-dagen opbouwen (40-urenweek)', kort: 'ATV' },
  /** `tvtWerkurenEnabled` / emmer `TVT_WERK`. */
  overuren: { label: 'Overuren sparen (TVT)', kort: 'Gespaarde overuren' },
  /** `tvtReisurenEnabled` / emmer `TVT_REIS`. */
  reistijd: { label: 'Reistijd sparen (TVT)', kort: 'Gespaarde reistijd' },
  /** `tvt*OpbouwPercentage` = 50. */
  half: { label: 'Half sparen, half uitbetalen', kort: '½ sparen' },
  /** `tvt*OpbouwPercentage` = 100. */
  alles: { label: 'Alles sparen', kort: 'Alles sparen' },
  /** TVT uit. */
  uitbetalen: { label: 'Uitbetalen', kort: 'Uitbetalen' },
  /** Saldo-emmer. */
  spaarpot: { label: 'Spaarpot', kort: 'Saldo' },
  /** Verzamelnaam voor het TVT-saldo (overuren + reistijd samen). */
  gespaardeUren: { label: 'Gespaarde uren (TVT)', kort: 'Gespaard (TVT)' },
} as const

/** Wat er met overuren of reistijd gebeurt (FO-344 §3.2, woordkeuze). */
export const SPAAR_KEUZES = ['UITBETALEN', 'HALF', 'ALLES'] as const
export type SpaarKeuze = (typeof SPAAR_KEUZES)[number]

export const SPAAR_KEUZE_LABELS: Record<SpaarKeuze, string> = {
  UITBETALEN: WERKTIJD_SPAREN_LABELS.uitbetalen.label,
  HALF: WERKTIJD_SPAREN_LABELS.half.label,
  ALLES: WERKTIJD_SPAREN_LABELS.alles.label,
}

export const SPAAR_KEUZE_KORT_LABELS: Record<SpaarKeuze, string> = {
  UITBETALEN: WERKTIJD_SPAREN_LABELS.uitbetalen.kort,
  HALF: WERKTIJD_SPAREN_LABELS.half.kort,
  ALLES: WERKTIJD_SPAREN_LABELS.alles.kort,
}

/** De drie spaarpotten die de uitleg toont (de klassieke emmer-types). */
export const SPAARPOT_SOORTEN = ['ATV', 'TVT_WERK', 'TVT_REIS'] as const
export type SpaarpotSoort = (typeof SPAARPOT_SOORTEN)[number]

export const SPAARPOT_LABELS: Record<SpaarpotSoort, string> = {
  ATV: WERKTIJD_SPAREN_LABELS.atv.kort,
  TVT_WERK: WERKTIJD_SPAREN_LABELS.overuren.kort,
  TVT_REIS: WERKTIJD_SPAREN_LABELS.reistijd.kort,
}

/**
 * Schermlabel voor een saldo-emmer of verlof-type: de spaarpotten met het korte
 * label uit de woordenlijst (`SPAARPOT_LABELS`, tegels en kolommen), de overige
 * uit de meegegeven gedeelde labelmap. Alleen voor de schermen van portal en
 * backoffice: exports en loonstrook-koppelingen houden de vaktermen uit
 * `verlof.ts` (`LEAVE_TYPE_LABELS`, `VERLOF_METHODE_LABELS`, de saldo-labelmaps),
 * zodat geen bestaande export van inhoud verandert.
 */
export function spaarpotSchermLabel(type: string, gedeeld: Readonly<Record<string, string>>): string {
  if (type === 'ATV' || type === 'TVT_WERK' || type === 'TVT_REIS') return SPAARPOT_LABELS[type]
  return gedeeld[type] ?? type
}

/**
 * Schermlabel voor een verlofmethode: TVT heet op de schermen "Gespaarde uren
 * (TVT)" (FO-344 §3.4); de rest komt uit de meegegeven gedeelde labelmap.
 */
export function verlofMethodeSchermLabel(methode: string, gedeeld: Readonly<Record<string, string>>): string {
  if (methode === 'TVT') return WERKTIJD_SPAREN_LABELS.gespaardeUren.label
  return gedeeld[methode] ?? methode
}

// ── Invoer van de uitleg ─────────────────────────────────────────────────────

/** De velden van het verlofbeleid (`VerlofBeleid`) die de uitleg nodig heeft. */
export interface WerktijdSparenBeleidDTO {
  /** ATV-opbouw per jaar bij een 38u-contract met 40u werken (default 104). */
  atvUrenPerJaar: number
  /** Maximum per spaarpot in werkweken (default 1). */
  tvtWerkurenMaxWeken: number
  tvtReisurenMaxWeken: number
  /** Overdracht naar volgend jaar in werkweken contracturen (default 1). */
  tvtMaxOverdrachtWeken: number
  /** Percentage van het maximum waarboven een pot "bijna vol" is (default 80). */
  tvtNotificatieDrempel: number
}

/**
 * De werktijd- en spaarinstellingen van één medewerker, zoals ze uit de
 * actieve contractmutatie en het verlofbeleid komen. Dezelfde vorm als de
 * invoer van `beschrijfWerktijdSparen`, zodat de portal de rekenvoorbeelden
 * van de keuzehulp met de eigen getallen kan uitrekenen.
 */
export interface WerktijdSparenInvoerDTO {
  contractUren: number
  /** Expliciete override van de werkuren per week (`werkUrenPerWeek`), anders null. */
  werkUrenPerWeek: number | null
  werkdagenPerWeek: number
  atvEnabled: boolean
  atvHoursPerWeek: number
  tvtWerkurenEnabled: boolean
  tvtReisurenEnabled: boolean
  /** 50 of 100. */
  tvtWerkOpbouwPercentage: number
  tvtReisOpbouwPercentage: number
  beleid: WerktijdSparenBeleidDTO
}

// ── Uitvoer van de uitleg ────────────────────────────────────────────────────

/** Eén regel op de kaart: een hoofdzin in de je-vorm met toelichting en statuslabel. */
export interface WerktijdSparenZinDTO {
  titel: string
  toelichting: string
  status: string
}

export const WERKTIJD_SPAREN_WAARSCHUWING_CODES = [
  /** Legacy: TVT staat aan terwijl ATV uit staat. De factor is 0, er wordt niets gespaard. */
  'TVT_ZONDER_ATV',
  /** ATV staat aan, maar het contract levert geen ATV-dagen op (alleen 38u -> 40u). */
  'ATV_ZONDER_OPBOUW',
] as const
export type WerktijdSparenWaarschuwingCode = (typeof WERKTIJD_SPAREN_WAARSCHUWING_CODES)[number]

export interface WerktijdSparenWaarschuwingDTO {
  code: WerktijdSparenWaarschuwingCode
  /** `HR` = alleen in de backoffice tonen; `IEDEREEN` = ook op de portal-kaart. */
  doelgroep: 'HR' | 'IEDEREEN'
  tekst: string
}

export interface WerktijdSparenBeschrijvingDTO {
  /** De drie zinnen voor de portal (je-vorm). */
  zinnen: {
    werktijd: WerktijdSparenZinDTO
    overuren: WerktijdSparenZinDTO
    reistijd: WerktijdSparenZinDTO
  }
  /** Dezelfde matrix in de derde persoon, voor de uitlegregels in de backoffice. */
  hrZinnen: {
    werktijd: string
    overuren: string
    reistijd: string
  }
  /**
   * Wat er FEITELIJK gebeurt (via `tvtFactor`): bij de legacy-combinatie
   * "TVT aan zonder ATV" is dat uitbetalen, ook al staat de schakelaar aan.
   */
  keuzes: {
    atv: boolean
    overuren: SpaarKeuze
    reistijd: SpaarKeuze
  }
  getallen: {
    contractUren: number
    werkweekUren: number
    atvUrenPerWeek: number
    atvUrenPerJaar: number
    atvDagenPerJaar: number
    daguren: number
    maxSparenOveruren: number
    maxSparenReistijd: number
    maxOverdrachtUren: number
    /**
     * ISO-datum (yyyy-mm-dd) waarop de spaarpotten van het lopende jaar vervallen:
     * 31 december, uit dezelfde bron als het echte verval (`vervalDatumVoorType`).
     * ATV vervalt dan helemaal; gespaarde overuren en reistijd (TVT) op het
     * overdrachtsmaximum na.
     */
    vervaldatum: string
    drempelPct: number
  }
  waarschuwingen: WerktijdSparenWaarschuwingDTO[]
}

/** Rekenvoorbeeld bij een keuze (FO-344 §3.2, §9). */
export interface WerktijdSparenRekenvoorbeeldDTO {
  uitbetaaldUren: number
  gespaardUren: number
  /** Over hoeveel uur de overwerktoeslag (de premie) wordt uitbetaald. */
  toeslagOverUren: number
  tekst: string
}

/** De ATV/TVT-vlaggen van een VERLOFOPBOUW-verzoek (de bestaande payload-vorm). */
export interface VerlofOpbouwVlaggenDTO {
  atvOpbouw: boolean
  tvtWerk: boolean
  tvtReis: boolean
  tvtWerkPercentage: 50 | 100
  tvtReisPercentage: 50 | 100
}

/** Eén spaarpot met voortgang naar het maximum. */
export interface WerktijdSparenSpaarpotDTO {
  soort: SpaarpotSoort
  label: string
  /** Uren in de pot (opgebouwd minus opgenomen, uit de saldo-ledger). */
  saldo: number
  /** Maximum per pot; `null` voor ATV (geen maximum). */
  max: number | null
  /** Percentage van het maximum (0 of meer), `null` zonder maximum. */
  pct: number | null
  /** Vanaf de beleidsdrempel (`tvtNotificatieDrempel`, default 80 procent). */
  bijnaVol: boolean
  vol: boolean
}

export interface WerktijdSparenOpenVerzoekDTO {
  id: string
  ingediendOp: string
  gevraagd: VerlofOpbouwVlaggenDTO
  /** In gewone taal wat er verandert, bijvoorbeeld "overuren half sparen, half uitbetalen". */
  samenvatting: string
  toelichting: string | null
}

export interface WerktijdSparenGeplandDTO {
  /** Ingangsdatum (yyyy-mm-dd, Europe/Amsterdam-kalenderdag). */
  vanaf: string
  beschrijving: WerktijdSparenBeschrijvingDTO
}

/**
 * Waarden voor de placeholders in de handleiding `atv-tvt-uitleg.md`. Als
 * opgemaakte NL-tekst ("38", "12,5", "31-12-2026").
 *
 * `contracturen`, `werkweekZonderAtv`, `voorbeeldWerkweek`, `maxSparen*` en
 * `maxOverdracht` zijn altijd de eigen getallen. De ATV-tokens (`atvContracturen`, `atvWerkweekUren`,
 * `atvUrenPerWeek`, `atvUrenPerJaar`, `atvDagenPerJaar`) beschrijven de
 * ATV-regeling met de eigen getallen zolang het eigen contract ATV oplevert
 * (38u -> 40u); anders de standaardregeling, omdat ATV alleen in die vorm
 * bestaat. Vervaldatum en vervalzinnen gelden voor iedereen.
 */
export interface HandleidingTokenWaardenDTO {
  contracturen: string
  /** De werkweek als ATV uit staat (de contracturen, of een afwijkende werkweek uit het contract). */
  werkweekZonderAtv: string
  /**
   * Zinsdeel voor "meer dan ..." in het overuren-voorbeeld: de eigen werkweek, met
   * en zonder ATV als die verschillen ("je werkweek (met ATV 40 uur, zonder ATV 38
   * uur)"), anders één getal ("je werkweek van 32 uur"). "Met ATV" is bij wie ATV
   * al aan heeft de huidige instelling (ook bij de gepinde groep: 38).
   */
  voorbeeldWerkweek: string
  /**
   * Hele zin over waar overuren beginnen: met ATV zijn de ATV-uren geen overuren;
   * staat ATV aan zonder opbouw (bv. de gepinde groep), dan is elk uur boven de
   * eigen werkweek een overuur.
   */
  overurenGrens: string
  /**
   * Hoeveel werkweken het maximum is, uit het beleid (`tvt*MaxWeken`): "één
   * werkweek", "2 werkweken", of beide als overuren en reistijd verschillen.
   */
  maxSparenOmvang: string
  /**
   * Waar het maximum op slaat, uit dezelfde tak als het getal: "met ATV" (de
   * werkweek met ATV-opbouw, 40), "jouw werkweek" (geen opbouw, werkweek gelijk aan
   * de contracturen, bv. 32 of de gepinde 38) of "je contracturen" (geen opbouw en
   * een afwijkende werkweek, bv. 38 uur contract met 39 uur werken).
   */
  maxSparenBasis: string
  /** Contracturen in de ATV-regeling (eigen contract, of de standaard 38). */
  atvContracturen: string
  /**
   * De werkweek in de ATV-regeling (40 bij 38 uur contract). Niet de eigen
   * werkweek van nu: die staat op de kaart (`getallen.werkweekUren`).
   */
  atvWerkweekUren: string
  atvUrenPerWeek: string
  atvUrenPerJaar: string
  atvDagenPerJaar: string
  /**
   * Maximum gespaarde overuren met ATV aan (sparen kan alleen met ATV): 40 bij 38
   * uur contract, anders de contracturen (parttime 32). Heeft de medewerker ATV al
   * aan, dan de huidige instelling (gelijk aan het maximum op de kaart, ook bij de
   * gepinde groep: 38); zonder ATV wat een ATV-aanvraag oplevert (40, terwijl de
   * kaart bij de huidige instelling 38 toont).
   */
  maxSparen: string
  /** Maximum gespaarde reistijd, zelfde regel als `maxSparen`. */
  maxSparenReistijd: string
  maxOverdracht: string
  vervaldatum: string
  /** Hele zin: ATV vervalt helemaal op de vervaldatum (geen overdracht). */
  vervalAtv: string
  /** Hele zin: gespaarde overuren en reistijd vervallen, op het overdrachtsmaximum na. */
  vervalTvt: string
}

/** `GET /me/werktijd-sparen` (portal) en het veld `uitleg` op het verlofprofiel. */
export interface WerktijdSparenDTO {
  /** `NIET_INGESTELD` = geen actieve contractmutatie (nog geen contract of in onboarding). */
  status: 'INGESTELD' | 'NIET_INGESTELD'
  invoer: WerktijdSparenInvoerDTO | null
  beschrijving: WerktijdSparenBeschrijvingDTO | null
  /** De huidige vlaggen in de payload-vorm, voor het vooraf invullen van de keuzehulp. */
  huidig: VerlofOpbouwVlaggenDTO | null
  spaarpotten: WerktijdSparenSpaarpotDTO[]
  /** Vervaldatum van de spaarpotten van het lopende jaar (yyyy-mm-dd, 31 december). */
  vervaldatum: string
  openVerzoek: WerktijdSparenOpenVerzoekDTO | null
  gepland: WerktijdSparenGeplandDTO | null
  tokens: HandleidingTokenWaardenDTO
}

// ── Legacy-rapport "TVT aan zonder ATV" (FO-344 §4.3) ────────────────────────

export interface TvtZonderAtvRijDTO {
  employeeId: string
  naam: string
  personeelsnummer: string | null
  /** De actieve contractmutatie met de ongeldige combinatie. */
  contractMutationId: string
  ingangsdatum: string
  tvtWerkurenEnabled: boolean
  tvtReisurenEnabled: boolean
  /** Huidig saldo gespaarde overuren en reistijd (blijft staan bij opschonen). */
  saldoOveruren: number
  saldoReistijd: number
  /** Er staat al een latere (geplande) contractmutatie: opschonen gaat dan handmatig. */
  heeftGeplandeMutatie: boolean
}

export interface TvtZonderAtvRapportDTO {
  rijen: TvtZonderAtvRijDTO[]
  totaal: number
}

export const opschonenTvtZonderAtvSchema = z.object({
  employeeIds: z.array(idParam()).min(1, 'Kies minstens een medewerker').max(500),
})
export type OpschonenTvtZonderAtvInput = z.infer<typeof opschonenTvtZonderAtvSchema>

export const TVT_OPSCHONEN_UITKOMSTEN = ['OPGESCHOOND', 'OVERGESLAGEN'] as const
export type TvtOpschonenUitkomst = (typeof TVT_OPSCHONEN_UITKOMSTEN)[number]

export interface TvtOpschonenResultaatRijDTO {
  employeeId: string
  naam: string
  uitkomst: TvtOpschonenUitkomst
  /** Bij OPGESCHOOND: de nieuwe contractmutatie. */
  contractMutationId: string | null
  /** Bij OVERGESLAGEN: waarom (bijvoorbeeld een geplande contractmutatie). */
  reden: string | null
}

export interface TvtOpschonenResultaatDTO {
  opgeschoond: number
  overgeslagen: number
  rijen: TvtOpschonenResultaatRijDTO[]
}
